/**
 * Standard layout for a Developer installation guide: overview and
 * prerequisites, numbered install steps, then reference sections — with an
 * "On this page" rail (and a Production/UAT switch when the guide's snippets
 * differ per environment) beside the content.
 */
import { Fragment, useState } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  AlertTitle,
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link as RouterLink } from 'react-router-dom';
import { CopyButton, SectionLabel, monoFieldSx } from '@src/components/indexPage/primitives';
import { useTitle } from '@src/hooks/useTitle';
import type { GuideBlock, GuideEnvironment, GuideSection, PluginGuide, SnippetCode } from './types';

const ENVIRONMENT_LABELS: Record<GuideEnvironment, string> = { production: 'Production', uat: 'UAT' };

// Leaves room for the fixed app bar when a section is scrolled into view.
const SECTION_SCROLL_MARGIN = 88;

const inlineCodeSx = {
  ...monoFieldSx,
  px: 0.5,
  py: 0.125,
  borderRadius: 0.5,
  bgcolor: 'action.hover',
};

const scrollToSection = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const INLINE_MARK = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/;
const LINK_MARK = /^\[([^\]]+)\]\(([^)]+)\)$/;

/**
 * Renders the inline marks used in guide text: `code`, **bold**, and links —
 * [label](#section-id) scrolls within the guide, [label](/path) routes in-app.
 */
export const renderInline = (text: string) =>
  text.split(INLINE_MARK).map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
      return <Box key={index} component="code" sx={inlineCodeSx}>{part.slice(1, -1)}</Box>;
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    const link = LINK_MARK.exec(part);
    if (link) {
      const [, label, target] = link;
      return target.startsWith('#') ?
        (
          <Link key={index} component="button" variant="inherit" sx={{ verticalAlign: 'baseline' }} onClick={() => scrollToSection(target.slice(1))}>
            {label}
          </Link>
        ) :
          <Link key={index} component={RouterLink} to={target}>{label}</Link>;
    }
    return <Fragment key={index}>{part}</Fragment>;
  });

const resolveCode = (code: SnippetCode, environment: GuideEnvironment) =>
  typeof code === 'string' ? code : code[environment];

const hasEnvironmentSnippets = (guide: PluginGuide) =>
  guide.sections.some(({ blocks }) =>
    blocks.some(block => block.kind === 'code' && typeof block.code !== 'string'));

const CodeSnippet = ({ language, code, caption, environment }: {
  language: string;
  code: SnippetCode;
  caption?: string;
  environment: GuideEnvironment;
}) => {
  const perEnvironment = typeof code !== 'string';
  const source = resolveCode(code, environment);
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 0.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>
          {language}
        </Typography>
        {perEnvironment && (
          <Typography variant="caption" color="text.secondary">
            · 
            {' '}
            {ENVIRONMENT_LABELS[environment]}
          </Typography>
        )}
        {caption && <Typography variant="caption" color="text.secondary">
          ·
          {caption}
        </Typography>}
        <Box sx={{ flex: 1 }} />
        <CopyButton name="Snippet" value={source} />
      </Stack>
      <Box
        component="pre"
        sx={{ ...monoFieldSx, m: 0, p: 1.5, overflowX: 'auto', whiteSpace: 'pre', lineHeight: 1.6 }}
      >
        <code>{source}</code>
      </Box>
    </Paper>
  );
};

const GuideTable = ({ columns, rows }: { columns: string[], rows: string[][] }) => (
  <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map(column => <TableCell key={column}>{column}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <TableCell key={cellIndex} sx={{ verticalAlign: 'top' }}>{renderInline(cell)}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const GuideBlockView = ({ block, environment }: { block: GuideBlock, environment: GuideEnvironment }) => {
  switch (block.kind) {
    case 'text':
      return <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{renderInline(block.text)}</Typography>;
    case 'subheading':
      return <Typography variant="subtitle2" sx={{ pt: 1 }}>{block.text}</Typography>;
    case 'list':
      return (
        <Box component={block.ordered ? 'ol' : 'ul'} sx={{ m: 0, pl: 3 }}>
          {block.items.map(item => (
            <Typography key={item} component="li" variant="body2" sx={{ lineHeight: 1.7, mb: 0.5 }}>
              {renderInline(item)}
            </Typography>
          ))}
        </Box>
      );
    case 'code':
      return <CodeSnippet {...block} environment={environment} />;
    case 'table':
      return <GuideTable columns={block.columns} rows={block.rows} />;
    case 'callout':
      return (
        <Alert severity={block.tone} variant="outlined">
          {block.title && <AlertTitle>{block.title}</AlertTitle>}
          {renderInline(block.text)}
        </Alert>
      );
  }
};

const StepBadge = ({ number }: { number: number }) => (
  <Box
    aria-hidden
    sx={{
      width: 28,
      height: 28,
      flexShrink: 0,
      borderRadius: '50%',
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: 14,
    }}
  >
    {number}
  </Box>
);

const GuideSectionView = ({ section, stepNumber, environment }: {
  section: GuideSection;
  stepNumber?: number;
  environment: GuideEnvironment;
}) => (
  <Box component="section" id={section.id} sx={{ scrollMarginTop: SECTION_SCROLL_MARGIN }}>
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
      {stepNumber !== undefined && <StepBadge number={stepNumber} />}
      <Box>
        {stepNumber !== undefined && (
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>
            Step 
            {' '}
            {stepNumber}
          </Typography>
        )}
        <Typography variant="h6" component="h2" sx={{ fontSize: 17, fontWeight: 600 }}>
          {section.title}
        </Typography>
      </Box>
    </Stack>
    <Stack spacing={1.5} sx={{ pl: stepNumber !== undefined ? 5.5 : 0 }}>
      {section.blocks.map((block, index) => (
        <GuideBlockView key={index} block={block} environment={environment} />
      ))}
    </Stack>
  </Box>
);

const OnThisPage = ({ sections, stepNumbers }: { sections: GuideSection[], stepNumbers: Map<string, number> }) => (
  <Box component="nav" aria-label="On this page">
    <SectionLabel>On this page</SectionLabel>
    <Stack spacing={0.75}>
      <Link component="button" variant="body2" underline="hover" color="text.primary" sx={{ textAlign: 'left' }} onClick={() => scrollToSection('overview')}>
        Overview
      </Link>
      {sections.map(({ id, title }) => (
        <Link
          key={id}
          component="button"
          variant="body2"
          underline="hover"
          color="text.primary"
          sx={{ textAlign: 'left' }}
          onClick={() => scrollToSection(id)}
        >
          {stepNumbers.has(id) ? `${stepNumbers.get(id)}. ${title}` : title}
        </Link>
      ))}
    </Stack>
  </Box>
);

const PluginGuidePage = ({ guide }: { guide: PluginGuide }) => {
  const { title } = useTitle(guide.title);
  const [environment, setEnvironment] = useState<GuideEnvironment>('production');
  const showEnvironmentSwitch = hasEnvironmentSnippets(guide);

  const stepNumbers = new Map<string, number>();
  guide.sections.filter(({ step }) => step).forEach(({ id }, index) => stepNumbers.set(id, index + 1));

  return (
    <>
      <Title title={title} />
      <Box
        sx={{
          display: 'grid',
          gap: 4,
          // minmax(0, …) lets long snippets scroll inside their box instead of widening the page.
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 240px' },
          alignItems: 'start',
          py: 2,
        }}
      >
        <Stack spacing={4} sx={{ minWidth: 0, maxWidth: 880, order: { xs: 2, lg: 1 } }}>
          <Paper id="overview" variant="outlined" sx={{ p: 2.5, scrollMarginTop: SECTION_SCROLL_MARGIN }}>
            <Typography variant="h6" component="h2" sx={{ fontSize: 17, fontWeight: 600, mb: 1 }}>
              {guide.title}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 2 }}>{renderInline(guide.summary)}</Typography>
            <SectionLabel>Before you start</SectionLabel>
            <Stack spacing={0.75}>
              {guide.prerequisites.map(item => (
                <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.25 }} />
                  <Typography variant="body2">{renderInline(item)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
          {guide.sections.map(section => (
            <GuideSectionView
              key={section.id}
              section={section}
              stepNumber={stepNumbers.get(section.id)}
              environment={environment}
            />
          ))}
        </Stack>
        <Stack
          spacing={3}
          sx={{ order: { xs: 1, lg: 2 }, position: { lg: 'sticky' }, top: { lg: SECTION_SCROLL_MARGIN } }}
        >
          {showEnvironmentSwitch && (
            <Box>
              <SectionLabel>Environment</SectionLabel>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={environment}
                onChange={(_, next: GuideEnvironment | null) => next && setEnvironment(next)}
                aria-label="Snippet environment"
              >
                {(Object.keys(ENVIRONMENT_LABELS) as GuideEnvironment[]).map(value => (
                  <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 2 }}>
                    {ENVIRONMENT_LABELS[value]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Code snippets update to match. Test in UAT before going live.
              </Typography>
            </Box>
          )}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <OnThisPage sections={guide.sections} stepNumbers={stepNumbers} />
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default PluginGuidePage;
